import { useEffect, useRef, useState } from 'react';
import { businessRoutes } from '@/lib/route-map';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { useCanManageGbp } from '@/hooks/use-can-manage-gbp';
import { locationApi } from '@/lib/api-paths';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Unlink } from 'lucide-react';
import { TERMS } from '@pageboss/shared';
import { buttonVariants } from '@/components/ui/button';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

// Starter-tier lock screen (design brief §5.2). Deliberately no blurred
// fake preview behind it — that would be the fabricated demo data Task 13
// just deleted, wearing a disguise. A customer who connected on a higher
// tier and then downgraded still needs a live way to disconnect (their
// review PII would otherwise be trapped behind the lock, plan Task 12) —
// hasConnection comes from the server's cheap existence check on GET
// /api/gbp/:siteId's locked branch (Task 15 addition).
export function LockedPanel({ siteId, hasConnection }: { siteId: string; hasConnection: boolean }) {
  // The tenant is the canonical route's, proven by the server on the request
  // that rendered this. It is never inferred from the other identifier.
  const businessId = useBusinessId() ?? '';
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnected, setDisconnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const doneRef = useRef<HTMLParagraphElement | null>(null);
  // Disconnecting is owner-only (gbp:manage).
  const canManage = useCanManageGbp();

  // Success here replaces the whole disconnect block — including the button
  // that was focused — with a confirmation line. Without this, focus fell to
  // <body> and nothing was announced (only the failure path had role=alert).
  useEffect(() => {
    if (disconnected) doneRef.current?.focus();
  }, [disconnected]);

  async function disconnect() {
    setDisconnecting(true);
    setError(null);
    try {
      await api.delete(locationApi(businessId, siteId, 'gbp/connection'));
      setDisconnected(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not disconnect right now.');
    } finally {
      setDisconnecting(false);
    }
  }

  return (
    <div className="flex flex-col items-start gap-4 rounded-xl border border-border-subtle bg-card p-8">
      <div className="max-w-prose">
        <h2 className="text-xl font-semibold tracking-tight">
          Manage your {TERMS.googleProfile} from here
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          See your real Google reviews, performance numbers, and location details — all in one
          dashboard. Included on every paid plan.
        </p>
      </div>
      <Link to={businessRoutes.billing(businessId)} className={buttonVariants()}>
        Upgrade plan
        <ArrowUpRight className="h-4 w-4" />
      </Link>

      {hasConnection && !disconnected && canManage ? (
        <div className="mt-2 w-full max-w-prose border-t border-border-subtle pt-4">
          <p className="text-sm text-text-secondary">
            Your {TERMS.googleProfile} is still connected from before. You can disconnect it here —
            we'll stop showing your Google data and delete the reviews we stored, including reviewer
            names.
          </p>
          {error ? (
            <p className="mt-2 text-sm text-danger-fg" role="alert">
              {error}
            </p>
          ) : null}
          <button
            type="button"
            onClick={() => void disconnect()}
            disabled={disconnecting}
            className={cn(buttonVariants({ size: 'sm', variant: 'outline' }), 'mt-3')}
          >
            <Unlink className="h-4 w-4" aria-hidden />
            {disconnecting ? 'Disconnecting…' : `Disconnect ${TERMS.googleProfile}`}
          </button>
        </div>
      ) : null}

      {disconnected ? (
        <p
          ref={doneRef}
          tabIndex={-1}
          role="status"
          className="mt-2 text-sm text-text-secondary focus:outline-none"
        >
          Disconnected. Your Google review data has been removed.
        </p>
      ) : null}
    </div>
  );
}
